'use client';

import { ArticleCard } from '@/sanity/fetchArticles';
import 'swiper/css';
import { Scrollbar } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import HIHButton from '../base/HIHButton';
import HIHLink from '../base/Link';
import SingleArticleCard from '../base/SingleArticleCard';
import SingleProminentArticleCard from '../base/SingleProminentArticleCard';
import SingleArticleCardMobileHorizontal from '../base/SingleArticleCardMobileHorizontal';
import { useState } from 'react';
import { LoadMoreButton } from '../base/LoadMoreButton';
import Image from 'next/image';
import styles from './ArticlesGrid.module.scss';

export default function ArticlesGrid({
  articles,
  categoryLink,
  title,
  categoryLinkLabel,
  variant = 'normal',
  className,
  showHeader = true,
  limit = 0,
  mobileView = 'horizontal',
  showBottomLoadMore = false,
  priority = false,
}: {
  articles: ArticleCard[];
  categoryLink?: string;
  title?: string;
  categoryLinkLabel?: string;
  variant?: 'normal' | 'prominent';
  className?: string;
  showHeader?: boolean;
  limit?: number;
  mobileView?: 'vertical' | 'horizontal' | 'fullWidth';
  showBottomLoadMore?: boolean;
  priority?: boolean;
}) {
  const initialVisible = limit || 6;
  const [visibleArticles, setVisibleArticles] = useState(initialVisible);

  const showMoreArticles = () => {
    setVisibleArticles(articles.length);
  };

  const articlesToShow = articles.slice(0, visibleArticles);

  return (
    <div className={className}>
      {/* Desktop */}
      {showHeader && (
        <div className={styles.desktopHeader}>
          {title && <h2 className={styles.desktopHeaderTitle}>{title}</h2>}
          <HIHButton
            link={categoryLink}
            title={categoryLinkLabel ?? 'Mehr anzeigen'}
            variant="outline"
            className={styles.outlineButton}
            size="md"
          />
        </div>
      )}

      <div
        className={[
          styles.desktopGrid,
          variant === 'normal' ? styles.normal : styles.prominent,
        ].join(' ')}
      >
        {articlesToShow?.map((article: ArticleCard) =>
          variant === 'prominent' ? (
            <SingleProminentArticleCard article={article} key={article._id} priority />
          ) : (
            <SingleArticleCard article={article} key={article._id} prioriy />
          ),
        )}
      </div>

      {/* Mobile */}
      {mobileView === 'horizontal' && (
        <div className={styles.mobileHorizontal}>
          {showHeader && title && categoryLink && (
            <HIHLink href={categoryLink} className={styles.mobileHeader}>
              <div className={styles.mobileHeaderTitle}>{title}</div>
              <div className={styles.mobileHeaderIcon}>
                <Image
                  src="icons/chevron_right.svg"
                  width={24}
                  height={24}
                  alt="Arrow Right"
                  priority={priority}
                />
              </div>
            </HIHLink>
          )}
          <Swiper
            slidesPerView="auto"
            spaceBetween={12}
            scrollbar={{ el: '.custom-swiper-scrollbar', draggable: true }}
            className={['swiper', styles.swiperFull].join(' ')}
            modules={[Scrollbar]}
            slidesOffsetBefore={16}
            slidesOffsetAfter={16}
          >
            {articlesToShow?.map((article: ArticleCard) => (
              <SwiperSlide
                key={article._id}
                className={[styles.swiperSlide, 'articlesHighlights-swiper-slide'].join(
                  ' ',
                )}
              >
                <SingleArticleCard
                  article={article}
                  key={article._id}
                  prioriy={priority}
                  variant="secondary"
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      )}

      {mobileView === 'vertical' && (
        <div className={styles.mobileVertical}>
          {articlesToShow?.map((article: ArticleCard) => (
            <SingleArticleCardMobileHorizontal article={article} key={article._id} />
          ))}
        </div>
      )}

      <div className={styles.loadMoreWrapper}>
        {/* Load More button */}
        {showBottomLoadMore && visibleArticles < articles.length && (
          <LoadMoreButton onClick={showMoreArticles} />
        )}
      </div>
    </div>
  );
}
